import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  OnChangeFn,
} from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Item } from '@/app/types/item';
import { Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { DateRange } from 'react-day-picker';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown } from 'lucide-react';

// Define custom meta properties for columns
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean';
  options?: Array<{ label: string; value: string | number | boolean }>;
}

// Extend ColumnDef with custom meta
type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

interface ItemDataTableProps {
  data: Item[];
  columns: ExtendedColumnDef<Item>[];
  isLoading: boolean;
  pageCount?: number;
  page: number;
  setPage: (page: number) => void;
  onSortingChange?: (sorting: SortingState) => void;
  sortingState?: SortingState;
  onFilterChange?: (filters: Record<string, string | number | boolean | { from?: string; to?: string }>) => void;
  initialFilters?: Record<string, string | number | boolean | { from?: string; to?: string }>;
  columnFilters?: ColumnFiltersState;
  onSearchChange: (value: string) => void;
  searchValue: string;
}

export function ItemDataTable({
  data,
  columns,
  isLoading,
  pageCount = 0,
  page,
  setPage,
  onSortingChange,
  sortingState = [],
  onFilterChange,
  initialFilters,
  columnFilters: externalColumnFilters,
  onSearchChange,
  searchValue,
}: ItemDataTableProps) {
  const navigate = useNavigate();
  const [sorting, setSorting] = useState<SortingState>(sortingState);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(externalColumnFilters || []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  
  // Initialize column filters from initialFilters prop
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (!hasInitialized && initialFilters) {
      const filters: ColumnFiltersState = [];
      Object.entries(initialFilters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          filters.push({ id: key, value });
        }
      });
      setColumnFilters(filters);
      setHasInitialized(true);
    }
  }, [hasInitialized, initialFilters]);

  // Sync external column filters
  useEffect(() => {
    if (externalColumnFilters) {
      setColumnFilters(externalColumnFilters);
    }
  }, [externalColumnFilters]);

  // Sync sorting state
  useEffect(() => {
    setSorting(sortingState);
  }, [sortingState]);

  // Create a stable debounced filter change handler
  const debouncedFilterChange = useMemo(
    () => debounce((filters: Record<string, string | number | boolean | { from?: string; to?: string }>) => {
      onFilterChange?.(filters);
    }, 300),
    [onFilterChange]
  );

  // Handle filter changes
  useEffect(() => {
    // Skip the initial render to prevent clearing URL filters
    if (!hasInitialized) {
      return;
    }
    
    if (onFilterChange) {
      // Convert column filters to a format that can be used by the API
      const apiFilters: Record<string, string | number | boolean | { from?: string; to?: string }> = {};
      
      columnFilters.forEach((filter) => {
        const value = filter.value;
        if (value !== undefined && value !== null) {
          // Handle date range objects
          if (typeof value === 'object' && 'from' in value) {
            const dateRange = value as { from?: string; to?: string };
            // Only add if at least one date is set
            if (dateRange.from || dateRange.to) {
              apiFilters[filter.id] = dateRange;
            }
          }
          // Handle simple types
          else if (value !== '' && (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')) {
            apiFilters[filter.id] = value;
          }
        }
      });
      
      debouncedFilterChange(apiFilters);
    }
    
    // Cleanup function
    return () => {
      debouncedFilterChange.cancel();
    };
  }, [columnFilters, debouncedFilterChange, onFilterChange, hasInitialized]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const newSorting = updater instanceof Function ? updater(sorting) : updater;
    setSorting(newSorting);
    if (onSortingChange) {
      onSortingChange(newSorting);
    }
  };

  const table = useReactTable({
    data,
    columns,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: pageCount,
  });

  // Helper function to handle individual column filter changes
  const handleColumnFilterChange = (columnId: string, value: any) => {
    setColumnFilters((prev) => {
      const existingFilter = prev.find((filter) => filter.id === columnId);
      const otherFilters = prev.filter((filter) => filter.id !== columnId);

      if (value === undefined || value === null || value === '') {
        // Remove filter if value is empty
        return otherFilters;
      }

      if (existingFilter) {
        // Update existing filter
        return [...otherFilters, { id: columnId, value }];
      }

      // Add new filter
      return [...prev, { id: columnId, value }];
    });
  };

  // Get current filter value for a column
  const getFilterValue = (columnId: string) => {
    const filter = columnFilters.find((f) => f.id === columnId);
    return filter?.value ?? '';
  };

  // Reset all filters
  const handleResetFilters = () => {
    setColumnFilters([]);
    onSearchChange('');
    // Also trigger the filter change to clear URL params
    if (onFilterChange) {
      onFilterChange({});
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-end">
          {/* Column visibility dropdown */}
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  Columns <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {table
                  .getAllColumns()
                  .filter((column) => column.getCanHide())
                  .map((column) => {
                    return (
                      <DropdownMenuCheckboxItem
                        key={column.id}
                        className="capitalize"
                        checked={column.getIsVisible()}
                        onCheckedChange={(value) => column.toggleVisibility(!!value)}
                      >
                        {column.id}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Reset filters button */}
            {columnFilters.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleResetFilters}
                className="h-8 px-2 lg:px-3"
              >
                Reset
                <RotateCcw className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Column filters row */}
        <div className="flex flex-wrap gap-2">
          {columns
            .filter((column) => column.meta?.variant)
            .map((column) => {
              const meta = column.meta!;
              const columnId = (column as any).accessorKey as string;
              const filterValue = getFilterValue(columnId);

              if (meta.variant === 'select') {
                return (
                  <div key={columnId} className="flex flex-col gap-1">
                    <Select
                      value={String(filterValue || '')}
                      onValueChange={(value) => handleColumnFilterChange(columnId, value === 'all' ? undefined : value)}
                    >
                      <SelectTrigger className="w-[180px] h-8">
                        <SelectValue placeholder={`Filter ${meta.label || columnId}...`} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All</SelectItem>
                        {meta.options?.map((option) => (
                          <SelectItem key={String(option.value)} value={String(option.value) || 'undefined'}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              }

              if (meta.variant === 'text') {
                return (
                  <div key={columnId} className="flex flex-col gap-1">
                    <Input
                      placeholder={meta.placeholder || `Filter ${meta.label}...`}
                      value={String(filterValue || '')}
                      onChange={(e) => handleColumnFilterChange(columnId, e.target.value || undefined)}
                      className="w-[180px] h-8"
                    />
                  </div>
                );
              }

              if (meta.variant === 'number') {
                return (
                  <div key={columnId} className="flex flex-col gap-1">
                    <Input
                      type="number"
                      placeholder={meta.placeholder || `Filter ${meta.label}...`}
                      value={String(filterValue || '')}
                      onChange={(e) => handleColumnFilterChange(columnId, e.target.value || undefined)}
                      className="w-[180px] h-8"
                    />
                  </div>
                );
              }

              // Date range picker for created_at
              if (columnId === 'created_at' && meta.variant === 'dateRange') {
                const dateRangeValue = filterValue as { from?: string; to?: string } | '';
                const dateRange: DateRange | undefined = 
                  typeof dateRangeValue === 'object' && dateRangeValue?.from
                    ? {
                        from: new Date(dateRangeValue.from),
                        to: dateRangeValue.to ? new Date(dateRangeValue.to) : undefined,
                      }
                    : undefined;

                return (
                  <div key={columnId} className="flex flex-col gap-1">
                    <DateRangePicker
                      date={dateRange}
                      onDateChange={(range) => {
                        if (range?.from) {
                          handleColumnFilterChange(columnId, {
                            from: format(range.from, 'yyyy-MM-dd'),
                            to: range.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                          });
                        } else {
                          handleColumnFilterChange(columnId, undefined);
                        }
                      }}
                      className="w-[260px]"
                    />
                  </div>
                );
              }

              return null;
            })}
        </div>
      </div>

      {/* Data table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="flex items-center justify-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={(e) => {
                    // Check if the click is on an action button or its child
                    const target = e.target as HTMLElement;
                    const isActionClick = target.closest('[data-action-cell]') || 
                                        target.closest('button') || 
                                        target.closest('[role="menuitem"]');
                    
                    if (!isActionClick) {
                      const item = row.original;
                      if ((e.ctrlKey || e.metaKey) && window.api?.tabs) {
                        // Open in new tab
                        window.api.tabs.create(`/items/detail/${item.id}`, `Item: ${item.name}`);
                      } else {
                        // Navigate in current tab
                        navigate(`/items/detail/${item.id}`);
                      }
                    }
                  }}
                  className="cursor-pointer hover:bg-muted/50"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No items found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="flex-1 text-sm text-muted-foreground">
          {table.getFilteredSelectedRowModel().rows.length} of{' '}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page === 0}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <div className="text-sm font-medium">
            Page {page + 1} of {pageCount || 1}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= (pageCount || 1) - 1}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}